'use client';

import { DialogBase } from '../../../DialogBase/DialogBase';
import { TwoFaLayoutWrapper } from '../shared/TwoFaLayoutWrapper';
import { useCallback, useState } from 'react';
import { dismissTwoFaSetup } from '../../../../../auth';
import { useResetTwoFactorAuthState } from './utils/useResetTwoFactorAuthState';
import { ICodeResponseType, ResetTwoFaModalStep } from './utils/types';
import { ResetTwoFaInitialScreen } from './subPages/ResetTwoFaInitialScreen/ResetTwoFaInitialScreen';
import { ProgressIndicatorWithSteps } from '../../../ProgressIndicatorWithSteps/ProgressIndicatorWithSteps';
import { ResetTwoFaSelectResetMethod } from './subPages/ResetTwoFaSelectResetMethod/ResetTwoFaSelectResetMethod';
import { ResetTwoFaMethodAppCode } from './subPages/ResetTwoFaMethodAppCode/ResetTwoFaMethodAppCode';
import { ResetTwoFaConfirmationScreen } from './subPages/ResetTwoFaConfirmationScreen/ResetTwoFaConfirmationScreen';
import { ResetTwoFaMethodRecoveryCode } from './subPages/ResetTwoFaMethodRecoveryCode/ResetTwoFaMethodRecoveryCode';
import { useUserInfoContext } from '../../../../userinfo/UserInfoContext/UserInfoProvider';
import { InputCodePage } from '../shared/InputCodePage/InputCodePage';
import { RecoveryCodesPage } from '../SetupTwoFaModal/subPages/RecoveryCodesPage/RecoveryCodesPage';
import { ResetTwoFaScanQrCode } from './subPages/ResetTwoFaScanQrCode/ResetTwoFaScanQrCode';
import { ModalComponent } from '../../../ModalDrawerContext/ModalDrawerContext';
import { useHandleGqlErrorsWithSnackbar } from '../../../../utils/errors';
import { TwoFaScreenTranslations } from './utils/get2faTranslations';

const ResetTwoFaModal: ModalComponent<{
  translations: TwoFaScreenTranslations;
}> = ({ translations, onClose }) => {
  const [loading, setLoading] = useState(false);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [iCodeResponse, setICodeResponse] = useState<ICodeResponseType>(null);

  const { refreshUserInfo } = useUserInfoContext();

  const {
    currentPage,
    onContinue,
    currentStepNumber,
    setPage,
    recoveryCodes,
    setRecoveryCodes,
  } = useResetTwoFactorAuthState();

  const onDismiss = async () => {
    setLoading(true);

    if (!recoveryCodes) {
      const respDismissTwoFa = await dismissTwoFaSetup();

      if (respDismissTwoFa?.errors) {
        processErrors(respDismissTwoFa.errors);
      }
    }

    await refreshUserInfo();

    onClose();

    setLoading(false);
  };

  const isCloseDisabledInTheCorner =
    currentPage === ResetTwoFaModalStep.INITIAL ||
    currentPage === ResetTwoFaModalStep.RECOVERY_CODES_PAGE ||
    currentPage === ResetTwoFaModalStep.CONFIRMATION;

  const backToPreviousStep = useCallback(() => {
    switch (currentPage) {
      case ResetTwoFaModalStep.METHOD_RECOVERY:
        return () => setPage(ResetTwoFaModalStep.METHOD_SELECTION);

      case ResetTwoFaModalStep.METHOD_AUTHENTICATOR:
        return () => setPage(ResetTwoFaModalStep.METHOD_SELECTION);

      case ResetTwoFaModalStep.METHOD_SELECTION:
        return () => setPage(ResetTwoFaModalStep.METHOD_SELECTION);

      case ResetTwoFaModalStep.INPUT_CODE_PAGE:
        return () => setPage(ResetTwoFaModalStep.SCAN_QR_PAGE);

      default:
        return undefined;
    }
  }, [currentPage, setPage]);

  return (
    <DialogBase
      id="reset-2fa-modal"
      onClose={onDismiss}
      size="medium"
      visual="secondary"
      title={translations.reset2faTitle}
      onMobileFullScreen
      backAction={backToPreviousStep()}
      contentProps={{
        disableContentPadding: true,
      }}
      disableCloseButton={isCloseDisabledInTheCorner}
    >
      <ProgressIndicatorWithSteps
        currentStep={currentStepNumber}
        totalSteps={7}
      />
      <TwoFaLayoutWrapper>
        {currentPage === ResetTwoFaModalStep.INITIAL && (
          <ResetTwoFaInitialScreen
            onContinue={onContinue}
            onDismiss={onDismiss}
            dismissLoading={loading}
            translations={translations}
          />
        )}
        {currentPage === ResetTwoFaModalStep.METHOD_SELECTION && (
          <ResetTwoFaSelectResetMethod
            dismissLoading={loading}
            onSelectAuthenticator={() =>
              setPage(ResetTwoFaModalStep.METHOD_AUTHENTICATOR)
            }
            onSelectRecovery={() =>
              setPage(ResetTwoFaModalStep.METHOD_RECOVERY)
            }
            translations={translations}
          />
        )}
        {currentPage === ResetTwoFaModalStep.METHOD_AUTHENTICATOR && (
          <ResetTwoFaMethodAppCode
            onContinue={onContinue}
            dismissLoading={loading}
            bindICodeResponse={setICodeResponse}
            translations={translations}
          />
        )}
        {currentPage === ResetTwoFaModalStep.METHOD_RECOVERY && (
          <ResetTwoFaMethodRecoveryCode
            onContinue={onContinue}
            dismissLoading={loading}
            bindICodeResponse={setICodeResponse}
            translations={translations}
          />
        )}

        {currentPage === ResetTwoFaModalStep.SCAN_QR_PAGE && (
          <ResetTwoFaScanQrCode
            onContinue={onContinue}
            secretCode={iCodeResponse?.secret}
            qrCode={iCodeResponse?.url}
            translations={translations}
          />
        )}

        {currentPage === ResetTwoFaModalStep.INPUT_CODE_PAGE && (
          <InputCodePage
            onContinue={onContinue}
            bindRecoveryCodes={(newCode: string) => setRecoveryCodes(newCode)}
            actionVariant="reset"
            translations={translations}
          />
        )}
        {currentPage === ResetTwoFaModalStep.RECOVERY_CODES_PAGE && (
          <RecoveryCodesPage
            onContinue={onContinue}
            recoveryCodes={recoveryCodes}
            translations={translations}
          />
        )}

        {currentPage === ResetTwoFaModalStep.CONFIRMATION && (
          <ResetTwoFaConfirmationScreen
            onClose={onClose}
            translations={translations}
          />
        )}
      </TwoFaLayoutWrapper>
    </DialogBase>
  );
};

export { ResetTwoFaModal, type TwoFaScreenTranslations };
